'use client';

import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { readStoredValue, writeStoredValue } from '../../lib/storage';
import { useUIStore } from '../../stores/uiStore';

type TrialBannerProps = {
  show: boolean;
  daysLeft: number | null;
  mode?: 'personal' | 'team';
};

const DISMISS_KEY = 'agentteams-trial-banner-dismissed';
const DISMISS_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours

const isDismissed = (): boolean => {
  // 저장소 차단 환경에서는 닫힘 기억 없이 매번 노출한다 (WEB-R).
  const dismissed = readStoredValue(DISMISS_KEY);
  if (!dismissed) return false;
  return Date.now() - Number(dismissed) < DISMISS_COOLDOWN_MS;
};

type Tone = 'info' | 'warning' | 'error';

const getTone = (daysLeft: number): Tone => {
  if (daysLeft <= 1) return 'error';
  if (daysLeft <= 3) return 'warning';
  return 'info';
};

// daisyUI `alert-*` 솔리드는 글자색으로 `--color-*-content`를 쓰는데 color-tokens.css가
// 이를 정의하지 않아 daisyUI 기본값이 새고, 라이트/다크 양쪽에서 대비가 무너진다.
// (design-system.md "content 토큰 누수") → 칩 bg + fg 페어로만 톤을 표현한다.
const TONE_TOKENS: Record<Tone, { background: string; borderColor: string; accent: string }> = {
  info: {
    background: 'var(--chip-info-bg)',
    borderColor: 'color-mix(in srgb, var(--chip-info-fg) 28%, transparent)',
    accent: 'var(--chip-info-fg)',
  },
  warning: {
    background: 'var(--chip-warning-bg)',
    borderColor: 'color-mix(in srgb, var(--chip-warning-fg) 28%, transparent)',
    accent: 'var(--chip-warning-fg)',
  },
  error: {
    background: 'var(--chip-error-bg)',
    borderColor: 'color-mix(in srgb, var(--chip-error-fg) 28%, transparent)',
    accent: 'var(--chip-error-fg)',
  },
};

export const TrialBanner = ({ show, daysLeft, mode = 'personal' }: TrialBannerProps) => {
  const t = useTranslations('billing.trial.banner');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (show && daysLeft !== null && !isDismissed()) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [show, daysLeft]);

  const dismiss = useCallback(() => {
    writeStoredValue(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  }, []);

  if (!visible || daysLeft === null) return null;

  // `daysLeft <= 0`은 만료(EXPIRED) 상태다 — useBillingExposure가 남은 일수를 0으로 클램프해서 넘긴다.
  const message =
    daysLeft <= 0
      ? t('expired')
      : daysLeft <= 1
        ? t(mode === 'team' ? 'teamUrgent' : 'urgent')
        : t(mode === 'team' ? 'teamExpiring' : 'expiring', { days: daysLeft });

  const tone = TONE_TOKENS[getTone(daysLeft)];

  return (
    <div
      role="status"
      // 위 여백은 main의 p-4/md:p-6/lg:p-8에서 오므로 아래 여백을 같은 스케일로 맞춘다.
      // 패딩/모서리/본문 크기는 같은 화면의 카드(p-6, rounded-2xl, text-sm)에 맞춰
      // 배너만 밀도가 튀지 않게 한다.
      className="relative mb-4 flex flex-col gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center sm:gap-4 md:mb-6 lg:mb-8"
      style={{ background: tone.background, borderColor: tone.borderColor }}
    >
      <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="mt-0.5 h-5 w-5 shrink-0 sm:mt-0"
          style={{ color: tone.accent }}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        {/* 모바일에선 닫기 버튼이 우상단에 겹치므로 제목 줄만 자리를 비워 준다. */}
        <div className="min-w-0">
          <h3 className="pr-8 text-sm font-semibold sm:pr-0" style={{ color: 'var(--color-fg-strong)' }}>
            {t('title')}
          </h3>
          <p className="mt-1 text-sm leading-relaxed" style={{ color: 'var(--color-fg)' }}>
            {message}
          </p>
        </div>
      </div>
      <button
        type="button"
        className="btn btn-primary btn-sm w-full shrink-0 sm:w-auto"
        onClick={() => useUIStore.getState().openSubscribeModal()}
      >
        {t('cta')}
      </button>
      <button
        type="button"
        aria-label={t('dismiss')}
        title={t('dismiss')}
        className="btn btn-ghost btn-sm btn-circle absolute top-2 right-2 shrink-0 sm:static"
        style={{ color: 'var(--color-fg-muted)' }}
        onClick={dismiss}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};
